import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { toMoment } from '@fortress-technology-solutions/fortress-component-library/utils';
import { useLeaseSigningProvider } from '../../../hooks/useLeaseSigningProvider';
import LeaseService from '../../../services/leaseService';
import {
  useFetchAllHouseholdRentableItems,
  useFetchHouseholdPets,
} from './hooks.query';
import {
  getBaseOtherAmount,
  getRentableItemsProratedAmount,
  getRequiredChargesSignature,
} from './LeaseDataTabFormSections/utils';

const leaseService = new LeaseService();

// A Blue Moon lease has no Fortress packet. UNKNOWN keeps the section, as on trunk, because an
// unresolved provider must not change what the page shows.
export const shouldShowLeasePacket = ({
  documentManagementMvp,
  leaseProvider,
}) => Boolean(documentManagementMvp) && leaseProvider !== 'BLUEMOON';

export const useLeaseProviderState = ({ flags, selectedProperty, lease }) => {
  const { provider: leaseProvider, isUsable: providerIsUsable } =
    useLeaseSigningProvider({
      organizationId: selectedProperty?.organizationId,
      propertyId: selectedProperty?.id,
      leaseId: lease?.id,
      enabled: flags?.blueMoonLeaseSigning ?? false,
    });
  // The section stays mounted while hidden for Blue Moon, so the store keeps this lease's own
  // selection: saves never send another lease's addenda, and a switch back keeps the unsaved draft.
  const shouldMountLeasePacket = Boolean(flags?.documentManagementMvp);
  const shouldDisplayLeasePacket = shouldShowLeasePacket({
    documentManagementMvp: flags?.documentManagementMvp ?? false,
    leaseProvider,
  });
  // An unresolved provider may hide a Blue Moon lease, so its packet takes no edits. Saves still send
  // the loaded selection: a save that locks the lease snapshots it, and an omitted list snapshots none.
  const isLeasePacketReadOnly = leaseProvider === 'UNKNOWN';
  return {
    leaseProvider,
    providerIsUsable,
    shouldMountLeasePacket,
    shouldDisplayLeasePacket,
    isLeasePacketReadOnly,
  };
};

// A term of 'default' is the placeholder option value — no term is actually selected yet.
const NO_TERM_SELECTED = 'default';

// Debounce window for the DERIVED pricing date (see useRecommendedLeaseRent's "ADDENDUM" doc
// comment below, step 2). The redux-form field itself keeps committing on every keystroke — that
// is unrelated business logic this hook must not interfere with — only the query key waits this
// long after the last change before it's allowed to move.
const PRICING_DATE_SETTLE_MS = 300;

// moment parses a PARTIAL date string leniently rather than rejecting it — e.g. mid-typing
// "04/01/2026" can momentarily look like a complete, "valid" date with year 202 (moment.isValid()
// returns true for that; it's a real calendar date, just an absurdly incomplete one). A
// four-digit year is the cheapest reliable signal that the date the user is typing has actually
// finished, rather than being caught mid-parse.
const isPlausibleCompleteDate = (momentValue) =>
  Boolean(momentValue) && momentValue.isValid() && momentValue.year() >= 1000;

const isDateInputEmpty = (value) =>
  value === undefined || value === null || value === '';

// ENG-30334 — the date a RENEWAL is priced as of while this lease's own Lease Start Date is still
// empty, which is the state every renewal opens in. Emphatically NOT the household's move-in date:
// `cloneLeaseFromPrevious` copies that forward from the original tenancy, often years back, so
// pricing against it resolves a REBA matrix period that predates the pricing entirely — the lookup
// then matches nothing, the endpoint answers 200 with no price, and the rent silently keeps its
// stale value with no signal that anything failed.
//
// Mirrors the backend's `getRenewalPricingEffectiveDate` (Leases.js) exactly, including the
// boundary: a lease ending TODAY has not "already passed" — its renewal takes effect the day
// after — so the equality case belongs to the end-date + 1 branch (`isSameOrAfter`, not
// `isAfter`). Falls back to today when the current lease end date is missing or unparseable,
// which is what the backend does with an invalid endDate.
// Returns a 'YYYY-MM-DD' STRING, never a moment. `rawPricingDateInput` is compared by identity
// (`settledRawPricingDateInputRef.current !== rawPricingDateInput`) and is an effect dependency of
// the settle debounce below, so a fresh object on every render makes the input look permanently
// changed: the 300ms timer resets forever, `settledPricingDateKey` never commits, `queryEnabled`
// stays false and NO pricing request is ever issued. A string is value-compared, so two renders
// with the same inputs produce an identical key and the debounce settles exactly once — the same
// referential stability the other two branches get for free by returning raw form values.
const getRenewalPricingEffectiveDate = (currentLeaseEndDate) => {
  const today = toMoment(new Date());
  const asKey = (value) => value.format('YYYY-MM-DD');
  if (isDateInputEmpty(currentLeaseEndDate)) return asKey(today);
  const endMoment = toMoment(currentLeaseEndDate);
  if (!isPlausibleCompleteDate(endMoment)) return asKey(today);
  return endMoment.isSameOrAfter(today, 'day')
    ? asKey(endMoment.clone().add(1, 'day'))
    : asKey(today);
};

// ROUND 8 — see the ROUND 8 doc comment on useRecommendedLeaseRent for the full defect this
// guards against. `undefined` and `null`/`''` are NOT interchangeable for these three fields:
// moveInDate/startDate/leaseTermId all arrive together, in ONE commit, from redux-form's
// `formValues` — never gradually. So the only way all three can be `undefined` AT THE SAME TIME
// is that this render's `getFormValues('FormLeaseDataTab')` simply hasn't resolved yet (formValues
// itself is still undefined) — leaseId/unitId (parent props) can easily already be populated on
// that very same render. Once formValues have genuinely landed, an unset field is a real, known
// empty value (`null`, `''`, or `NO_TERM_SELECTED`) — never bare `undefined` again. A single field
// being `undefined` on its own proves nothing (a genuine edit can transiently put any ONE of them
// there, e.g. clearing Move-In Date while Start Date already governs pricing) — it is specifically
// ALL THREE at once that is the "formValues have not arrived" signal, not "the form has no data".
const isPricingFormValuesUnknown = (
  moveInDateValue,
  startDateValue,
  leaseTermIdValue,
) =>
  moveInDateValue === undefined &&
  startDateValue === undefined &&
  leaseTermIdValue === undefined;

// The signature an armed recalculation is raised for, and later checked against (see step 3 in
// the doc comment below): the three inputs that actually determine what price comes back.
// leaseId is deliberately excluded — a lease switch already resets everything below via the
// leaseId-keyed effect, so folding it in here would be redundant, not safer.
const buildPricingSignature = (unitId, settledDateKey, leaseTermId) =>
  `${unitId ?? ''}::${settledDateKey ?? ''}::${leaseTermId ?? ''}`;

/**
 * Encapsulates all derived state and side-effects required to keep
 * `proratedOther` in sync whenever any of its contributing values change:
 *   - lease start date
 *   - non-rent recurring charges
 *   - monthly pet rent × pet count
 *   - short term fee (only when the selected term is ≤ the property threshold)
 *   - rentable items (prorated by their own date windows)
 *
 * Returns stable values that the parent component needs to pass down to child
 * sections so they can also recalculate on immediate user interactions
 * (e.g. typing a new rent amount in MonthlyLeaseCharges).
 */
export const useProratedOtherCalculation = ({
  flags,
  selectedProperty,
  householdId,
  formValues,
  leaseTerms,
  leaseId,
  monthlyRecurringCharges,
  setProratedOtherAmount,
  change,
}) => {
  const { householdRentableItems, householdRentableItemsUpdatedAt } =
    useFetchAllHouseholdRentableItems({
      enabled: flags?.automatedLeaseChargesP1,
      organizationId: selectedProperty?.organizationId,
      propertyId: selectedProperty?.id,
      householdId,
    });

  const { petCount, householdPetsUpdatedAt } = useFetchHouseholdPets({
    enabled: flags?.automatedLeaseChargesP1,
    organizationId: selectedProperty?.organizationId,
    propertyId: selectedProperty?.id,
    householdId,
  });

  // Stable primitive deps — prevent infinite render loops from object identity changes
  const startMoment = toMoment(formValues?.startDate);
  const monthlyPetRentAmount = parseFloat(formValues?.monthlyPetRent) || 0;
  const shortTermFeeAmount = parseFloat(formValues?.shortTermFee) || 0;
  const requiredChargesSignature = getRequiredChargesSignature(
    monthlyRecurringCharges,
  );

  const shortTermLeaseThreshold =
    selectedProperty?.config?.shortTermLeaseThreshold ?? null;
  const selectedLeaseTermMonths =
    leaseTerms?.find((lt) => lt.id === formValues?.leaseTermId)?.masterLeaseTerm
      ?.nMonths ?? null;
  const isShortTermLease =
    shortTermLeaseThreshold !== null &&
    selectedLeaseTermMonths !== null &&
    selectedLeaseTermMonths <= shortTermLeaseThreshold;

  const recalculateProratedOther = () => {
    if (!flags?.automatedLeaseChargesP1 || !startMoment) {
      return;
    }

    const baseOtherAmount = getBaseOtherAmount({
      monthlyRecurringCharges,
      monthlyPetRent: formValues?.monthlyPetRent,
      shortTermFee: formValues?.shortTermFee,
      petCount,
      isShortTermLease,
    });

    const rentableItemsProratedAmount = getRentableItemsProratedAmount(
      householdRentableItems,
      startMoment,
    );

    setProratedOtherAmount({
      startDate: startMoment,
      otherAmount: baseOtherAmount,
      updateFn: (val) => {
        const baseProratedAmount = parseFloat(val);
        const safeBaseAmount = isNaN(baseProratedAmount)
          ? 0
          : baseProratedAmount;
        const finalAmount = (
          safeBaseAmount + rentableItemsProratedAmount
        ).toFixed(2);
        change('proratedOther', finalAmount);
      },
    });
  };

  useEffect(() => {
    recalculateProratedOther();
    // setProratedOtherAmount, change and resetProratedRentAutoFilledChangeStatus
    // are stable references intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    monthlyPetRentAmount,
    shortTermFeeAmount,
    householdRentableItemsUpdatedAt,
    petCount,
    householdPetsUpdatedAt,
    isShortTermLease,
  ]);

  // Saving the Required Non-Rent Charges refetches them, and the new charges
  // re-seed the form from the saved lease, which resets Prorated Other. A save
  // arms one recalculation that runs when this lease's refetched charges arrive,
  // even if their total is unchanged. Nothing else arms it, so opening the tab,
  // switching leases and saving the lease itself keep their existing behavior.
  // Returns a cancel for a save that failed, so it can't fire on a later refetch.
  const pendingChargesSave = useRef(null);
  const [chargesSaveRecalculations, setChargesSaveRecalculations] = useState(0);
  const onRequiredChargesSave = useCallback(() => {
    const pending = { leaseId, signature: requiredChargesSignature };
    pendingChargesSave.current = pending;
    return () => {
      if (pendingChargesSave.current === pending) {
        pendingChargesSave.current = null;
      }
    };
  }, [leaseId, requiredChargesSignature]);

  useEffect(() => {
    const pending = pendingChargesSave.current;
    if (!pending) {
      return;
    }
    if (pending.leaseId !== leaseId) {
      pendingChargesSave.current = null;
      return;
    }
    if (pending.signature === requiredChargesSignature) {
      return;
    }
    pendingChargesSave.current = null;
    setChargesSaveRecalculations((count) => count + 1);
  }, [leaseId, requiredChargesSignature]);

  // Runs one render later, so the calculation reads the values the reload
  // re-seeded (an unsaved Lease Start Date is discarded by it, not used here).
  useEffect(() => {
    if (chargesSaveRecalculations > 0) {
      recalculateProratedOther();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargesSaveRecalculations]);

  return {
    householdRentableItems,
    petCount,
    isShortTermLease,
    onRequiredChargesSave,
  };
};

export const useAsyncMonthlyLeaseTransactions = (leaseId) => {
  const queryClient = useQueryClient();

  const fetchLeaseMonthlyTransactions = async ({ queryKey }) => {
    const [, id] = queryKey;
    if (!id) return [];

    const abortController = new AbortController();
    const options = { signal: abortController.signal };

    try {
      return await leaseService.getMonthlyLeaseTransactionsForResident(
        id,
        options,
      );
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Request was aborted');
      }
      throw error;
    }
  };

  const { data: allMonthlyTransactions = [] } = useQuery(
    ['monthlyLeaseTransactions', leaseId],
    fetchLeaseMonthlyTransactions,
    {
      enabled: !!leaseId,
      refetchOnWindowFocus: false,
    },
  );

  const updateTransactionsList = () => {
    queryClient.invalidateQueries(['monthlyLeaseTransactions', leaseId]);
  };

  return {
    allMonthlyTransactions,
    updateTransactionsList,
  };
};

/**
 * ENG-30333 — recommends a lease rent from REBA's pricing (New or Renewal — the backend decides
 * which, see leaseService#getNewLeasePricing), keyed on the unit / move-in date / start date /
 * lease term currently selected on the Lease Data tab, and applies it to the editable
 * `leasedRent` field — but ONLY on a genuine user edit to one of those tracked inputs, and only
 * while the lease is still editable and unlocked. Behind `reba-rev-mgmt`.
 *
 * THIS IS THE THIRD REWRITE OF THIS TRIGGER. The previous two both tried to infer "the user edited
 * an input" by DIFFING VALUES across renders (a baseline snapshot, keyed or not by leaseId), and
 * both failed adversarial validation: `moveInDate`, `startDate` and `leaseTermId` arrive via
 * redux-form's `formValues` (through a `connect()` that WRAPS `reduxForm()`), which lands ONE
 * COMMIT LATER than `leaseId`/`unitId` (parent props) on every mount and every header lease
 * switch. Any value-diff rule — no matter how it's baselined — eventually scores that later
 * commit (redux-form delivering the NEW lease's own persisted values) as a user edit, because it
 * is, structurally, indistinguishable from one by looking at values alone. See the hooks.test.js
 * suite's "real mount sequence" tests for the reproduction.
 *
 * THE FIX: STOP INFERRING. `notifyTrackedInputChanged` (returned below) is the ONLY thing that can
 * arm a recalculation, and it is called EXCLUSIVELY from the per-`<Field>` `onChange` prop of the
 * three tracked inputs (LeaseBasics/rightColumn.js) — i.e. redux-form's `ConnectedField.handleChange`
 * invoking it directly from a real `input.onChange` call, never from a value comparison. That
 * prop-level `onChange` is verified (real store, real redux-form, real `<Field>`s) to fire on every
 * genuine edit — including a custom component calling `input.onChange(momentObj)` with no DOM
 * event, e.g. the date pickers here — and NEVER on mount, INITIALIZE, re-INITIALIZE (a lease
 * switch), or a programmatic `change()`/`dispatch(change())`. There is no second trigger path:
 * `leaseId`/`unitId`/`moveInDate`/`startDate`/`leaseTermId` below are read ONLY to build the query
 * and to know when a fetched price is safe to apply — never compared against their own history.
 *
 * Tracking and sending are different concerns (Finding 4): `moveInDate` and `startDate` are BOTH
 * tracked inputs — either one's `<Field>` firing `onChange` must be eligible to recalculate — but
 * only ONE of them is the date actually SENT to the pricing endpoint, and which one depends on
 * `isRenewal` (ENG-31245): a NEW lease prices off `moveInDate` — ENG-30333 names "the Unit,
 * Move-In Date, and Lease Term" as the pricing inputs and never mentions Start Date — while a
 * RENEWAL prices off `startDate` instead, because `moveInDate` there is the household's ORIGINAL
 * move-in, copied by `cloneLeaseFromPrevious`, often years in the past; pricing a renewal against
 * it would resolve the wrong REBA matrix period. `isRenewal` is read straight from the same
 * `lease.isRenewal` rightColumn.js already uses to disable the Move-In Date `<Field>` for a
 * renewal — not a new flag. A container that pre-merges the two dates into a single value before
 * calling this hook collapses that distinction and makes a Move-In Date edit invisible whenever
 * `startDate` is already set — this hook keeps them separate for tracking and merges only for the
 * query (`rawPricingDateInput` below).
 *
 * Why a "pending" flag at all, rather than just reacting to the query result: the recommended
 * price for a NEW input generally isn't available until a LATER render (the query has to
 * refetch), so `notifyTrackedInputChanged` only ARMS `pendingRecalcRef`; a second effect watches
 * the fetched price and applies it once an authoritative one for the (by then current) inputs
 * arrives, then clears the flag. Without the flag, any authoritative price landing while the tab
 * is simply open — e.g. on load, once the query resolves for whatever is already saved — would
 * get (re)applied to `leasedRent`, which is exactly Scenario 3/4's forbidden case.
 *
 * Finding 6 — a pending recalculation must not outlive what raised it:
 *   - `pendingRecalcRef` is unconditionally dropped the moment `leaseId` changes (mount, remount,
 *     or a header lease switch) — a recalculation armed for lease A has no bearing on lease B, and
 *     the query key already includes `leaseId` so a stale price literally can't arrive under the
 *     new key, but this also stops a dangling flag from firing later against whatever price for
 *     the new lease happens to resolve first.
 *   - `pendingRecalcRef` is also dropped whenever `queryEnabled` (the same gate the query itself
 *     uses) goes false — Scenario 5, so a lease that gets locked mid-edit can't apply a
 *     recalculation that was already in flight when the price eventually lands.
 *   - a manual edit to `leasedRent` (via `clearRecommendedRent`, called from MonthlyLeaseCharges'
 *     `onChangeLeaseRentAmount` on every manual edit) also drops it — the user just told the form
 *     what the rent is; a REBA price that was mid-flight for an earlier tracked-input edit must
 *     not silently overwrite that a moment later.
 *
 * `notifyTrackedInputChanged` itself does NOT gate on `queryEnabled` — it only ever arms the flag,
 * never applies anything, and the apply effect below re-checks `queryEnabled` freshly against the
 * CURRENT (post-edit) render before ever calling `onRecalculate`. Gating the arm itself would
 * reintroduce exactly the bug Finding 5 fixed: at the moment a `<Field>`'s `onChange` fires,
 * `moveInDate`/`startDate`/`leaseTermId` (and therefore `queryEnabled`) still reflect the
 * PRE-edit render — so a first-time lease-term selection (empty -> set, required by Scenario 2)
 * would see `queryEnabled: false` (no term selected YET) and never arm.
 *
 * FINDING 1 — the arm must die with the fetch that answered it, not wait for a future one: a
 * pending recalculation was previously cleared ONLY on leaseId change, `!queryEnabled`, a manual
 * rent edit, or a successful apply — never on a fetch that simply RESOLVED WITHOUT an
 * authoritative price. So editing Lease Term to a value REBA has no price for left the arm
 * standing, and it was later consumed by whatever price a completely unrelated BACKGROUND refetch
 * happened to bring back (production builds a bare `new QueryClient()`, so react-query v3
 * defaults apply: refetchOnWindowFocus/refetchOnReconnect true, staleTime 0 — a window-focus
 * refetch alone was enough to silently overwrite `leasedRent`). The fix distinguishes "hasn't
 * resolved yet" from "resolved, nothing authoritative" using `isFetching`: the apply effect below
 * only acts once `isFetching` is false for the CURRENT query key, and when it settles that way
 * with no authoritative price, it clears `pendingRecalcRef` right there instead of leaving it
 * armed for whatever fetch happens to run next.
 *
 * ADDENDUM (round 5) — WHY THE PREVIOUS FIX FOR "one keystroke, one request" WAS WRONG: an earlier
 * attempt debounced the tracked `<Field>` handlers themselves (rightColumn.js), which (a) wrapped
 * PRE-EXISTING, unrelated business logic — the automatedLeaseChargesP1 prorated-rent recompute on
 * Start Date change — in a delay it never had before, dropping it on unmount or a non-moment
 * value; (b) armed the recalculation from a bare `setTimeout` via a REF mutation, which schedules
 * no React render, so the arm sat dormant until some unrelated later render happened to consume
 * it; and (c) didn't even fix the reported problem, because `updateLeaseDate` (the redux-form
 * commit) stayed synchronous regardless — every keystroke still landed in `formValues`, so
 * `pricingDateKey` (computed directly from `formValues` on every render) still churned once per
 * character. THE FORM must keep updating on every keystroke — that's how typing works, and other
 * logic depends on it. What must NOT churn is the PRICING QUERY, and that settling belongs HERE,
 * in the hook that owns the query — not in the field handlers.
 *
 * Three changes implement that:
 *
 * STEP 2 — SETTLE THE DERIVED PRICING DATE, NOT THE INPUTS. `moveInDate`/`startDate` are read
 * fresh every render (still churning every keystroke, unchanged) but the value actually placed in
 * the query key — `settledPricingDateKey` — is produced by a real `useState` updated from a
 * `useEffect`/`setTimeout` pair (a genuine debounce that schedules a re-render via `setState`, NOT
 * a ref mutation that schedules nothing). A rapid burst of keystrokes cancels-and-reschedules that
 * timer every render (the effect's cleanup clears the previous `setTimeout`), so only the LAST
 * value in an uninterrupted burst is ever allowed to settle. `isPlausibleCompleteDate` is the
 * defense-in-depth this needs on top of plain `.isValid()`: moment happily parses a half-typed
 * "04/01/2026" into an "isValid()" moment with an absurd year (e.g. 202), so the settle callback
 * additionally requires a four-digit year before committing — a partial or implausible date
 * simply leaves the PREVIOUS settled value in place (an explicitly cleared date is different: an
 * empty input settles to `null`, since that's a real, complete state, not a partial one).
 * `isPricingDateSettling` mirrors `isFetching` for this internal debounce, so the apply effect
 * (step 3) can tell "hasn't finished debouncing yet" apart from "settled, and unchanged".
 *
 * STEP 3 — THE ARM REMEMBERS WHAT IT WAS RAISED FOR. A bare boolean arm can't tell "this fetch
 * answers MY edit" from "this fetch is just the stale answer for a key my edit never touched", so
 * any settled fetch for a query key an edit couldn't actually move (e.g. editing Move-In Date
 * while `startDate` already governs pricing — Finding 4) would read as confirmation of a fresh
 * price and (re)apply the STALE cached price sitting under that unchanged key. Checking the
 * settled `unitId`/`settledPricingDateKey`/`leaseTermId` signature against a remembered baseline
 * fixes that half of the problem — see ROUND 7 below for how that baseline itself is now kept
 * honest, which six prior rounds of patching it in place failed to do.
 *
 * STEP 4 — see index.js: `onRecalculate` is now a `useCallback`, not an inline arrow, so the apply
 * effect below re-runs because one of ITS OWN inputs changed, not because the parent happened to
 * re-render for an unrelated reason.
 *
 * ROUND 7 — THE BASELINE ITSELF CANNOT BE ALLOWED TO GO STALE. Every round through the sixth
 * patched WHEN the baseline gets resynced (on a lease switch, once settled inputs "belong" to the
 * lease, etc.), and every round left a window where that resync didn't happen in time: for up to
 * PRICING_DATE_SETTLE_MS after a page load or a header lease switch, `settledPricingDateKey` is
 * still catching up to the current lease. An edit ARMED inside that window
 * (`pendingRecalcRef.current = true`) used to BLOCK the resync outright (the old code only
 * resynced the baseline when nothing was pending), so the baseline sat on a stale/mixed signature
 * until the debounce timer eventually fired — and it was that timer firing, not any user edit,
 * that then read as "the signature changed" and applied a cached price for an edit that, on its
 * own, could not have moved anything. Reproduced by parametrising WHEN the edit lands after a
 * switch/load: +0/+100/+250ms — always wrongly applied; +400ms, once the window had closed on its
 * own — correctly a no-op.
 *
 * The fix removes the resync bookkeeping rather than patching it again. One rule replaces it,
 * tracked per `leaseId` via `isBaselineEstablishedRef`/`baselineSignatureRef` (both reset by the
 * leaseId-keyed effect below): THE FIRST SETTLE OBSERVED FOR A LEASE ESTABLISHES THE BASELINE. A
 * "settle" here means the derived pricing date has stopped debouncing AND the settled inputs are
 * confirmed to belong to the CURRENT lease (`!leaseIdJustChanged && settledDateBelongsToCurrentLease`)
 * AND the query isn't fetching — deliberately NOT the full `queryEnabled`, which also folds in
 * isLocked/isEditable/isLeaseRentEditable/hasSelectedTerm/the flag. None of those bear on whether
 * the SIGNATURE is trustworthy yet, and gating establishment on them too would mean a lease that
 * starts with no term selected (Finding 5's scenario) could never establish a baseline before the
 * user's first genuine term selection — and that first selection, which must recalculate, would
 * wrongly be swallowed as "the first settle" instead.
 *
 * Once established, the baseline can only move forward from an ARMED edit whose settled signature
 * actually differs from it — exactly STEP 3's check — so there is no longer a window where the
 * baseline and the arm can disagree about which lease, or which prior edit, they belong to: the
 * baseline is never written from anything but a settled signature.
 *
 * ENG-31246 — ROUND 7, as originally written, discarded an arm raised before the first settle
 * (`pendingRecalcRef.current = false`, no apply) on the theory that no price change could be
 * "honestly attributed" to an edit made while the pricing inputs were still catching up. In
 * practice this threw away a genuine user edit's ALREADY-FETCHED, correct price: `pendingRecalcRef`
 * can only be raised by an explicit `notifyTrackedInputChanged()` call from a tracked `<Field>`'s
 * onChange — a real user action, never the mount-order replay this baseline mechanism exists to
 * suppress (that replay never calls notify) — so there is nothing dishonest about honouring it. The
 * fix: when the arm is already raised at the settle that establishes the baseline, apply the
 * fetched price for the (now-settled, now-current) inputs instead of discarding it. STEP 3's
 * signature-equality check is skipped for that one settle — the baseline was just seeded FROM this
 * same signature, so it would otherwise always "match" regardless of whether the edit itself moved
 * anything, and masking a genuine, explicitly-armed edit that way is exactly the bug this fixes.
 *
 * ROUND 8 — ROUND 7's "first settle" rule assumed a "settle" and "the inputs are still unknown"
 * were the same kind of gap it already handled (the lease-switch one-render lag covered by
 * `leaseIdJustChanged`). They are not, on PAGE LOAD (and every remount — the lease is typically
 * already in the redux store when the tab mounts, so this is not mount-only trivia): render 1
 * resolves `leaseId`/`unitId` from PARENT PROPS, while `getFormValues('FormLeaseDataTab')` is
 * STILL undefined — so `startDate`/`moveInDate`/`leaseTermId` are all undefined THAT RENDER. The
 * query is therefore disabled (no settled date, no term), so `isFetching` is false, so
 * `isPricingSignatureSettled` was true — and ROUND 7's rule adopted THAT render as the first
 * settle, establishing an EMPTY baseline (`unit-1||undefined`) before the form's real, one-commit-
 * later values ever arrived. `leaseIdJustChanged` does not catch this: it is seeded from the SAME
 * render's `leaseId` (`useRef(leaseId)`), so on a genuine mount — where there is no PREVIOUS
 * leaseId to differ from — it reads false from the very first render, unlike a header switch where
 * a real previous value already sits in the ref.
 *
 * The fix is NOT to widen `isFetching`, add a timer, or special-case "the first render" — every one
 * of those reintroduces the exact ambiguity ROUND 7 already removed once. Instead,
 * `isPricingFormValuesUnknown` (see its doc comment above) makes "we have a settled signature"
 * actually mean it: `moveInDate`/`startDate`/`leaseTermId` arrive from redux-form in a single
 * commit, so `undefined` on all three simultaneously is structurally distinguishable from a
 * genuinely empty, ARRIVED signature (`null`/`''`/`NO_TERM_SELECTED` are real, known values a
 * settled render can have — bare `undefined` on all three is not "empty", it's "not yet known").
 * `isPricingSignatureSettled` folds this in alongside the existing checks, so render 1's
 * (genuinely unknown) inputs can no longer be mistaken for a settled empty one, and the REAL first
 * settle — once formValues have landed AND the pricing date has finished debouncing — is what
 * establishes the baseline, exactly as ROUND 7 intended.
 *
 * `persistedPricingSource` seeds the applied amount/provider (instead of `null`) so a saved lease
 * shows its provider on open without a live pricing request, preserving the no-reprice-on-open
 * invariant above.
 */
export const useRecommendedLeaseRent = ({
  enabled,
  organizationId,
  propertyId,
  leaseId,
  unitId,
  moveInDate,
  startDate,
  leaseTermId,
  isRenewal,
  currentLeaseEndDate,
  isEditable,
  isLocked,
  isLeaseRentEditable,
  persistedPricingSource,
  onRecalculate,
}) => {
  // STEP 2 — the RAW date sent to pricing: the date THIS lease's rent takes effect. ENG-31245 —
  // for a renewal, moveInDate is disabled in the UI (rightColumn.js) and holds the household's
  // ORIGINAL move-in date, not this lease's effective date, so a renewal prices off startDate.
  // ENG-30334 — when that startDate is still empty (the state EVERY renewal opens in, before the
  // user picks one) the fallback is the derived renewal effective date, NOT moveInDate: falling
  // back to moveInDate re-opened the exact wrong-period bug ENG-31245 fixed, because the original
  // move-in is typically years before any REBA pricing exists, so the lookup matched nothing and
  // the rent silently stayed stale. For a NEW lease, moveInDate IS the pricing input ENG-30333 names, so it wins
  // whenever it's present AT ALL — even mid-typed and not yet parseable — rather than falling
  // back to startDate the instant it looks invalid: that fallback-on-invalid would make the
  // effective key bounce over to startDate on almost every keystroke of an in-progress Move-In
  // Date edit, which is exactly the per-keystroke churn this whole addendum exists to stop. This
  // raw value is never used as the query key directly — only its SETTLED counterpart below is.
  const rawPricingDateInput = isRenewal
    ? isDateInputEmpty(startDate)
      ? getRenewalPricingEffectiveDate(currentLeaseEndDate)
      : startDate
    : isDateInputEmpty(moveInDate)
    ? startDate
    : moveInDate;

  const [settledPricingDateKey, setSettledPricingDateKey] = useState(() => {
    if (isDateInputEmpty(rawPricingDateInput)) {
      return null;
    }
    const initialMoment = toMoment(rawPricingDateInput);
    return isPlausibleCompleteDate(initialMoment)
      ? initialMoment.format('YYYY-MM-DD')
      : null;
  });

  // True from the moment the raw pricing date input changes until PRICING_DATE_SETTLE_MS later,
  // when the debounce below either commits a new settled key or gives up on an incomplete one.
  // The apply effect (step 3) must not conclude "nothing changed" while this is true — otherwise a
  // genuine date edit could be judged a no-op before its own debounce ever got a chance to run.
  //
  // This MUST be computed synchronously, during render, by comparing against a ref — the same
  // pattern `leaseIdJustChanged` below uses — rather than via a `useState` set from inside an
  // effect. A render's effects all read THAT render's own closure; a `setState` made from one
  // effect is not visible to another effect in the SAME commit, only on the NEXT one. So a
  // `useState` here would make the apply effect below observe "not settling" for one extra render
  // after `rawPricingDateInput` actually changes — long enough, on the very render an edit lands,
  // to wrongly treat the (still-stale) previous signature as already confirmed and clear the arm
  // for nothing. Comparing refs during render has no such lag.
  const settledRawPricingDateInputRef = useRef(rawPricingDateInput);
  const isPricingDateSettling =
    settledRawPricingDateInputRef.current !== rawPricingDateInput;

  // FINDING F FIX — which lease's arrival `settledPricingDateKey` was last resolved for.
  // `settledPricingDateKey` is a debounced `useState`: on a header lease switch it keeps the
  // OUTGOING lease's date for up to PRICING_DATE_SETTLE_MS after `leaseId`/`unitId`/`leaseTermId`
  // have already moved on to the new lease (formValues arrive one commit after leaseId, and this
  // key settles a further 300ms after that). Without tracking this separately, `queryEnabled`
  // below has no way to tell "settled for THIS lease" apart from "still settled for the PREVIOUS
  // one" — which is exactly how a real query key was observed combining lease B's id/unit/term
  // with lease A's still-settled date. Updated only inside the debounce's own timer (below),
  // never compared against a snapshot of its own history — same discipline as
  // `notifyTrackedInputChanged` itself.
  const settledDateLeaseIdRef = useRef(leaseId);
  const settledDateBelongsToCurrentLease =
    settledDateLeaseIdRef.current === leaseId;

  // Guarantees a re-render when the debounce below finishes even on the no-op case (a partial/
  // invalid date, or clearing an already-empty one) where `settledPricingDateKey` doesn't actually
  // change value and so wouldn't otherwise trigger one — a bare ref mutation alone schedules
  // nothing, which is exactly how the previous, rejected fix left an arm dormant indefinitely
  // (see the ADDENDUM doc comment above).
  const [, forceSettleRerender] = useState(0);
  const isFirstPricingDateRunRef = useRef(true);

  useEffect(() => {
    // The very first render's ref/state above already agree (both seeded from the same initial
    // raw input) — nothing to debounce yet, and no reason to hold the initial query back by
    // PRICING_DATE_SETTLE_MS on page load / a header lease switch.
    if (isFirstPricingDateRunRef.current) {
      isFirstPricingDateRunRef.current = false;
      return undefined;
    }

    const timer = setTimeout(() => {
      if (isDateInputEmpty(rawPricingDateInput)) {
        // The date was cleared outright — that IS a real, complete state (no date), not a partial
        // one, so it settles, to nothing.
        setSettledPricingDateKey(null);
      } else {
        const candidate = toMoment(rawPricingDateInput);
        if (isPlausibleCompleteDate(candidate)) {
          setSettledPricingDateKey(candidate.format('YYYY-MM-DD'));
        }
        // else: partial or invalid (e.g. the mid-typing year-0202 case) — leave the previous
        // settled value in place. There is nothing complete yet to replace it with.
      }
      settledRawPricingDateInputRef.current = rawPricingDateInput;
      // FINDING F FIX — this timer is the moment the settle actually happens FOR `leaseId` (the
      // value closed over by THIS run of the effect, captured below in the dep array). Recording
      // it here, and only here, is what lets `settledDateBelongsToCurrentLease` above tell a
      // caught-up settle apart from one still trailing a lease that's already been switched away
      // from.
      settledDateLeaseIdRef.current = leaseId;
      forceSettleRerender((n) => n + 1);
    }, PRICING_DATE_SETTLE_MS);

    return () => clearTimeout(timer);
    // FINDING F FIX — `leaseId` is deliberately included alongside `rawPricingDateInput`: a lease
    // switch must always re-run this debounce, even in the (rare) case where the new lease's raw
    // date input happens to be textually identical to the outgoing lease's (e.g. both null) —
    // otherwise `settledDateLeaseIdRef` would never advance past the outgoing lease, permanently
    // stranding `settledDateBelongsToCurrentLease` at `false` and disabling pricing for the new
    // lease for good, even once the user makes a genuine edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawPricingDateInput, leaseId]);

  const hasSelectedTerm =
    Boolean(leaseTermId) && leaseTermId !== NO_TERM_SELECTED;

  // FINDING 3 — leaseId/unitId (props) and moveInDate/startDate/leaseTermId (formValues) do NOT
  // arrive in the same render on a header lease switch — see this hook's top doc comment for why
  // (connect() wraps reduxForm(); formValues land one commit later). So the FIRST render after
  // leaseId changes still carries the PREVIOUS lease's dates/term alongside the NEW leaseId/
  // unitId: without this guard that render's queryKey would combine them and fire a real pricing
  // request for a lease/unit/date/term combination that was never actually selected — a wrong-
  // input request hitting a money endpoint, even though (thanks to Finding 1's fix) nothing from
  // it is ever applied. `leaseIdJustChanged` is true for exactly that one render; by the very next
  // render the documented one-commit-later arrival guarantees formValues belong to the current
  // lease, so the suppression lifts itself without ever needing to compare formValues directly.
  const previousLeaseIdRef = useRef(leaseId);
  const leaseIdJustChanged = previousLeaseIdRef.current !== leaseId;
  useEffect(() => {
    previousLeaseIdRef.current = leaseId;
  }, [leaseId]);

  // FINDING 6 — must mirror the EXACT condition the Lease Rent <Field> itself uses to decide
  // whether it's writable (see getLeaseRentField, MonthlyLeaseCharges/index.js — the field is
  // unconditionally `disabled={true}` on a Public Housing floor plan, REGARDLESS of `isEditable`,
  // because that rent is program-set by formula). `isEditable`/`isLocked` alone are not enough:
  // an affordable lease can be editable and unlocked and still have a hard-disabled Lease Rent
  // field. Market pricing must never govern a formula-set rent, and gating on a re-derivation of
  // that condition (rather than the caller passing through the SAME value the field's `disabled`
  // prop is built from) would let the two drift apart. `isLeaseRentEditable` is that passthrough —
  // see index.js's call site for how it's computed.
  // ITEM 2 FIX (Finding F) — `settledDateBelongsToCurrentLease` closes the gap `leaseIdJustChanged`
  // alone leaves open: that flag only covers the SINGLE render where leaseId has changed but
  // formValues haven't caught up yet. formValues then arrive a render later, but the SETTLED date
  // key lags a further PRICING_DATE_SETTLE_MS behind THAT — so there is a real window, after
  // `leaseIdJustChanged` has already gone false, where `leaseId`/`unitId`/`leaseTermId` are the NEW
  // lease's while `settledPricingDateKey` is still the OUTGOING lease's. A query enabled in that
  // window is a real request to a money endpoint for inputs that never coexisted. Until the settle
  // has actually run for the CURRENT leaseId, nothing is issued.
  const queryEnabled = Boolean(
    enabled &&
      isEditable &&
      !isLocked &&
      isLeaseRentEditable &&
      !leaseIdJustChanged &&
      settledDateBelongsToCurrentLease &&
      organizationId &&
      propertyId &&
      // ENG-30333 — deliberately NOT `leaseId`. The Lease Data tab is reachable before the lease
      // is generated, and that pre-generation screen is where the rent PERSISTED at creation is
      // chosen; requiring a leaseId here meant the matrix was never consulted there and the
      // term-blind `Unit.quotingRentAmount` was what got saved — every lease term persisting the
      // same number. leaseService#getNewLeasePricing routes to the unit-scoped read when leaseId
      // is absent. The two lease-keyed guards above stay correct with no lease: both compare
      // against refs seeded from the same `undefined`, so neither ever reports a false "changed".
      unitId &&
      settledPricingDateKey &&
      hasSelectedTerm,
  );

  const { data, isFetching } = useQuery(
    [
      'newLeasePricing',
      organizationId,
      propertyId,
      leaseId,
      unitId,
      settledPricingDateKey,
      leaseTermId,
    ],
    () =>
      leaseService.getNewLeasePricing(organizationId, propertyId, leaseId, {
        unitId,
        moveInDate: settledPricingDateKey,
        leaseTermId,
      }),
    { enabled: queryEnabled },
  );

  // The backend returns `{ hasPricing, price: { termId, termMonths, amount, isAuthoritative,
  // provider: { id, name } | null } }` (see Leases.js#getLeasePricingForLease) — isAuthoritative,
  // amount and provider all live on the nested `price` object, not on the response body itself.
  const price = data?.hasPricing ? data.price : null;
  const recommendedRent = price?.isAuthoritative ? price.amount : null;
  const recommendedRentProviderName = price?.isAuthoritative
    ? price?.provider?.name ?? null
    : null;

  // The ONE trigger. Armed exclusively by `notifyTrackedInputChanged` (below) — never by
  // comparing this render's tracked values against a remembered previous set. See "STOP
  // INFERRING" in the doc comment above for why a second, value-diff-based path is exactly what
  // must not exist here.
  const pendingRecalcRef = useRef(false);

  // ROUND 7 — the baseline this arm is checked against, and whether one has been ESTABLISHED yet
  // for the CURRENT leaseId. See the ROUND 7 doc comment above for the full reasoning; in brief:
  // `isBaselineEstablishedRef` starts false for every lease (reset by the leaseId-keyed effect
  // below) and flips true the first time this hook observes a genuine settle for that lease — at
  // which point `baselineSignatureRef` is written from that settle and nothing is applied. Only
  // AFTER that can an armed edit's own later settle be compared against the baseline and, if
  // different, applied. Neither ref is ever seeded from a raw render-time signature the way its
  // predecessor (`lastPricingSignatureRef`) was — that seeding was the root cause this round
  // removes rather than patches again.
  const isBaselineEstablishedRef = useRef(false);
  const baselineSignatureRef = useRef(null);

  // Tracks the value actually written to `leasedRent`, as opposed to `recommendedRent` above
  // (the latest query result, which can be non-null before a recalculation is pending, or can
  // move on to a newer price for newer inputs while `leasedRent` still holds an older applied
  // one). The "Recommended Rent" label reads this instead, so it can never show a figure that
  // disagrees with what's actually in the Lease Rent field.
  const [appliedRecommendedRent, setAppliedRecommendedRent] = useState(
    () => persistedPricingSource?.quotedAmount ?? null,
  );
  // Companion to appliedRecommendedRent; set/cleared in lockstep with it.
  const [
    appliedRecommendedRentProviderName,
    setAppliedRecommendedRentProviderName,
  ] = useState(() => persistedPricingSource?.providerName ?? null);

  // Finding 6 / 8 — a recalculation armed for a DIFFERENT lease (or a recommendation applied for
  // one) has no bearing on this one: the form is never remounted (no key, enableReinitialize:
  // true), so switching leases in the header selector lands here as an in-place prop change, and
  // without this reset a pending flag raised right before the switch would be consumed by the
  // NEXT lease's price the moment it resolves. This runs on every leaseId change including first
  // mount, where both refs/state are already at these same defaults, so it's a no-op there.
  // ROUND 7 — `isBaselineEstablishedRef`/`baselineSignatureRef` reset alongside the arm, for the
  // same reason: a baseline established for the OUTGOING lease has no bearing on the new one
  // either. The main effect below re-establishes it from lease B's own first settle — it is never
  // seeded directly from this render's values, which (per Finding 3) are still a mixed lease-A/
  // lease-B signature at this exact point.
  // persistedPricingSource is deliberately omitted from deps: it changes together with leaseId on
  // a real lease switch, and re-running this on every unrelated re-render would re-clobber an
  // already-applied live recalculation with the stale persisted figure.
  useEffect(() => {
    pendingRecalcRef.current = false;
    setAppliedRecommendedRent(persistedPricingSource?.quotedAmount ?? null);
    setAppliedRecommendedRentProviderName(
      persistedPricingSource?.providerName ?? null,
    );
    isBaselineEstablishedRef.current = false;
    baselineSignatureRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaseId]);

  // Scenario 5, defense in depth: if recalculation becomes disallowed (lease gets locked, becomes
  // non-editable, or the flag flips off) while a recalc is still pending an in-flight price, drop
  // it — rather than letting it apply later if the lease somehow becomes editable again with the
  // same inputs still in place.
  useEffect(() => {
    if (!queryEnabled) {
      pendingRecalcRef.current = false;
    }
  }, [queryEnabled]);

  // ROUND 7 — a "settle" for the purposes of establishing or advancing the baseline: the derived
  // pricing date has stopped debouncing AND the settled inputs are confirmed to belong to THIS
  // leaseId. Deliberately narrower than `queryEnabled` — see the ROUND 7 doc comment above for why
  // folding isLocked/isEditable/isLeaseRentEditable/hasSelectedTerm in here would wrongly delay
  // establishment past a genuine first edit (Finding 5, a lease term picked for the first time).
  //
  // ROUND 8 — `!isPricingFormValuesUnknown(...)` closes the page-load/remount gap `leaseIdJustChanged`
  // leaves open (see the ROUND 8 doc comment above): on the render where leaseId/unitId are already
  // resolved from props but redux-form's formValues haven't landed yet, moveInDate/startDate/
  // leaseTermId are all `undefined` together — that is "not yet known", not a settled empty
  // signature, and must not count as this lease's first settle.
  const isPricingSignatureSettled =
    !leaseIdJustChanged &&
    settledDateBelongsToCurrentLease &&
    !isPricingDateSettling &&
    !isFetching &&
    !isPricingFormValuesUnknown(moveInDate, startDate, leaseTermId);

  useEffect(() => {
    if (!isPricingSignatureSettled) {
      // Either the settled inputs still trail the previous lease, the pricing-date debounce
      // hasn't finished, or the query itself hasn't resolved yet — nothing is final, so there is
      // nothing to establish or apply from this render.
      return;
    }

    const currentSignature = buildPricingSignature(
      unitId,
      settledPricingDateKey,
      leaseTermId,
    );

    const isFirstSettleForLease = !isBaselineEstablishedRef.current;
    if (isFirstSettleForLease) {
      // THE FIRST SETTLE FOR THIS LEASE. Adopt it as the baseline.
      baselineSignatureRef.current = currentSignature;
      isBaselineEstablishedRef.current = true;
      // ENG-31246 FIX — an arm raised here is NOT discarded. `pendingRecalcRef` can only have been
      // set by an explicit `notifyTrackedInputChanged()` call from a tracked `<Field>`'s onChange
      // (see "STOP INFERRING" in this hook's top doc comment) — a real user edit, never the
      // mount-order replay this baseline mechanism exists to suppress (that replay never calls
      // notify). The previous rule threw that edit's already-fetched, correct price away purely
      // because it happened to land before the lease's first settle finished — see ENG-31246.
      // Falling through to the shared apply logic below (rather than returning here) is what
      // honours it: `currentSignature === baselineSignatureRef.current` is skipped for exactly
      // this settle, since the baseline was just seeded FROM this signature and would otherwise
      // trivially "match" and mask a genuine edit.
    }

    if (!queryEnabled || !pendingRecalcRef.current) {
      // Established already, but either recalculation is currently disallowed (the dedicated
      // Scenario 5 / Finding 6 disarm effect above handles dropping the arm for that case) or no
      // edit is pending right now. Nothing to do either way.
      return;
    }

    // FINDING 1 — the fetch answered the edit that armed this flag, one way or the other: it dies
    // here regardless of the outcome, so an authoritative price a LATER, unrelated refetch brings
    // back for these same (still-current) inputs is never mistaken for a fresh user edit.
    pendingRecalcRef.current = false;

    // STEP 3 — only a settle for a signature that's actually DIFFERENT from the established
    // baseline counts as something new. An edit that couldn't move
    // unitId/settledPricingDateKey/leaseTermId settles right back to the SAME signature — there is
    // NO new price to apply, by construction, regardless of what the (unrefetched, stale) cached
    // `data` happens to hold for that unchanged key.
    // ENG-31246 — this check is deliberately skipped on the settle that establishes the baseline
    // (see above): the baseline was seeded FROM this exact signature moments ago, so the two would
    // always "match" here regardless of whether the user's edit itself moved anything, and an
    // explicit arm surviving to the first settle already proves it's a real edit worth honouring.
    if (
      !isFirstSettleForLease &&
      currentSignature === baselineSignatureRef.current
    ) {
      return;
    }
    baselineSignatureRef.current = currentSignature;

    if (recommendedRent == null) {
      return;
    }
    setAppliedRecommendedRent(recommendedRent);
    setAppliedRecommendedRentProviderName(recommendedRentProviderName);
    onRecalculate(recommendedRent);
    // `moveInDate`/`startDate` (the RAW tracked-field values, not just their derived
    // `settledPricingDateKey`) are deliberately included here. Without them, an edit that cannot
    // move the pricing signature — a Move-In Date edit while `startDate` already governs pricing
    // is the concrete case — changes none of this effect's OTHER deps either, so the effect never
    // reruns at all: the arm `notifyTrackedInputChanged` raised sits DORMANT, uncleared, until
    // some UNRELATED later dep change happens to run this effect next and consumes it for that
    // unrelated change instead. Listing the raw fields guarantees this effect reevaluates (and,
    // per the branches above, either correctly clears the arm or correctly applies nothing) on the
    // very same render the edit's own props land — no react-query refetch or extra render
    // required. This must stay a plain dependency, not a `useState` counter bumped from
    // `notifyTrackedInputChanged`: that alternative was tried and reverted — it schedules its own
    // render, which (called outside the SAME `act()`/batch as the prop update that follows it,
    // exactly how every call site here invokes it) fires this effect ONE render too early, against
    // the PRE-edit props, consuming the arm for nothing before the edit's actual new value ever
    // arrives.
  }, [
    isPricingSignatureSettled,
    queryEnabled,
    recommendedRent,
    recommendedRentProviderName,
    unitId,
    settledPricingDateKey,
    leaseTermId,
    moveInDate,
    startDate,
    onRecalculate,
  ]);

  // THE trigger itself. Call this from a tracked `<Field>`'s `onChange` prop — nothing else —
  // the moment the user actually edits Move-In Date or Lease Term (LeaseBasics/rightColumn.js).
  // NOT Unit: `unitId` is read below only to build the pricing signature/query key (it's part of
  // WHAT is priced), but nothing on the Lease Data tab lets a user change the unit on an existing
  // lease — LeaseBasics/leftColumn.js renders the unit number as static text, not a `<Field>`, and
  // its parent (LeaseBasics/index.js) doesn't even pass this callback down to it. A unit change
  // only happens via a separate transfer workflow (ResidentHeader → TransferButtons/handleTransfer)
  // that produces a new lease record entirely, outside this hook's render. Do not wire this call
  // to that flow on the strength of this comment alone — confirm nothing has changed first.
  // Deliberately does NOT gate on `queryEnabled`/`isEditable`/`isLocked` here (see the doc
  // comment's last paragraph): this only arms the flag, and the apply effect above re-validates
  // every gate fresh, against the POST-edit render, before ever calling `onRecalculate`. A
  // disabled `<Field>` also can't fire a user `onChange` in the first place, so this never runs
  // for a locked/non-editable lease regardless.
  const notifyTrackedInputChanged = useCallback(() => {
    pendingRecalcRef.current = true;
  }, []);

  // Finding 8 / Finding 6 — the label's "leasedRent already holds this figure" claim stops being
  // true the moment the user manually edits the rent afterward, AND a manual edit must cancel any
  // recalculation still in flight from an earlier tracked-input edit (otherwise a REBA price that
  // was already mid-fetch could land a moment later and silently overwrite what the user just
  // typed by hand). Callers must invoke this (MonthlyLeaseCharges' onChangeLeaseRentAmount does,
  // on every manual edit) so both guarantees hold.
  const clearRecommendedRent = useCallback(() => {
    pendingRecalcRef.current = false;
    setAppliedRecommendedRent(null);
    setAppliedRecommendedRentProviderName(null);
  }, []);

  return {
    recommendedRent: appliedRecommendedRent,
    recommendedRentProviderName: appliedRecommendedRentProviderName,
    clearRecommendedRent,
    notifyTrackedInputChanged,
  };
};
